import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Grid, IconButton, Modal, Paper, Tooltip, Typography } from "@mui/material";

import { axiosPost } from "api";
import ObservationRecordsModal, {
  ObservationRecordSavePayload,
} from "components/utils/ObservationRecordsModal";
import OrderArticleFormFields from "./OrderArticleFormFields";
import { MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL } from "constants/urls";
import { buildObservationPreviewSummary } from "helpers/movementArticleObservations";
import {
  buildObservationPreviewRecords,
  buildStockUnitSelection,
  normalizeObservationInput,
  ObservationPreviewUnit,
} from "helpers/stockUnitObservationSelection";
import { useToast } from "hooks/useToast";
import { MdVisibility } from "react-icons/md";
import {
  Article,
  Client,
  GroupedOutcomeObservationPreviewPayload,
  GroupedOutcomeObservationPreviewStockUnit,
  OrderDraftArticleRow,
} from "types";

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: OrderDraftArticleRow;
  articles: Article[];
  client: Client | null;
  readOnlyIdentityFields?: boolean;
  onEdit: (row: OrderDraftArticleRow) => void;
};

const prepareOrderArticleDraft = (row: OrderDraftArticleRow) => ({
  ...row,
  observation_preview_units: row.observation_preview_units?.map(
    (previewUnit: GroupedOutcomeObservationPreviewStockUnit & Partial<Pick<ObservationPreviewUnit, "original_observation">>) => ({
      ...previewUnit,
      original_observation: previewUnit.original_observation === undefined
        ? previewUnit.observation ?? null
        : previewUnit.original_observation,
    }),
  ),
});

export default function EditOrderArticleModal({
  open,
  handleOpen,
  value,
  articles,
  client,
  readOnlyIdentityFields = false,
  onEdit,
}: Props) {
  const { showSnackBar } = useToast();
  const [draft, setDraft] = useState(() => prepareOrderArticleDraft(value));
  const [observationModalOpen, setObservationModalOpen] = useState(false);
  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState<boolean>(false);

  useEffect(() => {
    setDraft(prepareOrderArticleDraft(value));
  }, [value]);

  const article = useMemo(
    () =>
      articles.find((currentArticle) => currentArticle.id === draft.article_id) ??
      null,
    [articles, draft.article_id],
  );

  const observationPreviewUnits = useMemo(
    () => draft.observation_preview_units ?? [],
    [draft.observation_preview_units],
  );

  const observationSummary = useMemo(
    () => buildObservationPreviewSummary(observationPreviewUnits) ?? "-",
    [observationPreviewUnits],
  );

  const hasValidAmount =
    Number.isInteger(Number(draft.amount)) && Number(draft.amount) > 0;

  const observationPreviewRecords = useMemo(
    () => buildObservationPreviewRecords(observationPreviewUnits),
    [observationPreviewUnits],
  );

  const handleChange = (
    field: keyof OrderDraftArticleRow,
    fieldValue: string | number | null,
  ) => {
    setDraft((prev) => ({
      ...prev,
      [field]: fieldValue,
      ...(field === "amount"
        ? {
            stock_unit_selection: undefined,
            observation_preview_units: undefined,
          }
        : {}),
    }));
  };

  const loadObservationPreviewUnits = useCallback(async (): Promise<
    ObservationPreviewUnit[] | null
  > => {
    if (!client?.id) {
      showSnackBar("Debe seleccionar un cliente.", "error");
      return null;
    }

    if (!draft.article_location_id) {
      showSnackBar(
        "La línea seleccionada no tiene article_location_id para revisar observaciones.",
        "error",
      );
      return null;
    }

    if (!hasValidAmount) {
      showSnackBar(
        "Selecciona una cantidad válida para revisar las observaciones.",
        "error",
      );
      return null;
    }

    if (observationPreviewUnits.length > 0) {
      return observationPreviewUnits;
    }

    setObservationRecordsLoading(true);

    const response = await axiosPost<GroupedOutcomeObservationPreviewPayload>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_OBSERVATION_PREVIEW_URL,
      {
        client_id: client.id,
        article_location_ids: [Number(draft.article_location_id)],
        amount: Number(draft.amount),
      },
      true,
    );

    setObservationRecordsLoading(false);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ??
          "No se pudieron obtener las unidades del pedido.",
        "error",
      );
      return null;
    }

    const previewUnits = (
      (response.data ?? []) as GroupedOutcomeObservationPreviewStockUnit[]
    ).map((previewUnit) => ({
      ...previewUnit,
      original_observation: previewUnit.observation ?? null,
    }));

    setDraft((prev) => ({
      ...prev,
      stock_unit_selection:
        previewUnits.length > 0
          ? buildStockUnitSelection(previewUnits)
          : undefined,
      observation_preview_units: previewUnits,
    }));

    return previewUnits;
  }, [
    client?.id,
    draft.amount,
    draft.article_location_id,
    hasValidAmount,
    observationPreviewUnits,
    showSnackBar,
  ]);

  const handleOpenObservationModal = useCallback(async () => {
    const previewUnits = await loadObservationPreviewUnits();

    if (!previewUnits) {
      return;
    }

    setObservationModalOpen(true);
  }, [loadObservationPreviewUnits]);

  const handleSaveObservationRecord = async ({
    recordId,
    observation,
    quantityMode,
    specificQuantity,
  }: ObservationRecordSavePayload) => {
    const targetRecord = observationPreviewRecords.find(
      (record) => record.id === recordId,
    );

    if (!targetRecord) {
      showSnackBar(
        "No se encontró el grupo de observaciones seleccionado.",
        "error",
      );
      return;
    }

    const targetQuantity =
      quantityMode === "specific"
        ? Number(specificQuantity ?? 0)
        : targetRecord.stock_unit_ids.length;

    if (
      !Number.isFinite(targetQuantity) ||
      targetQuantity <= 0 ||
      targetQuantity > targetRecord.stock_unit_ids.length
    ) {
      showSnackBar("La cantidad específica seleccionada no es válida.", "error");
      return;
    }

    const nextObservation = normalizeObservationInput(observation);
    const affectedStockUnitIds = new Set(
      targetRecord.stock_unit_ids.slice(0, targetQuantity),
    );

    setDraft((prev) => {
      const nextPreviewUnits = (prev.observation_preview_units ?? []).map(
        (previewUnit) =>
          affectedStockUnitIds.has(previewUnit.id)
            ? {
                ...previewUnit,
                observation: nextObservation,
              }
            : previewUnit,
      );

      return {
        ...prev,
        stock_unit_selection:
          nextPreviewUnits.length > 0
            ? buildStockUnitSelection(nextPreviewUnits)
            : undefined,
        observation_preview_units: nextPreviewUnits,
      };
    });
  };

  const handleSubmit = () => {
    onEdit(draft);
    handleOpen();
  };

  const disabled =
    !draft.article_id ||
    !hasValidAmount ||
    Number(draft.amount_per_container) <= 0;

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "48rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Editar articulo del pedido
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <OrderArticleFormFields
              value={draft}
              articles={articles}
              client={client}
              readOnlyIdentityFields={readOnlyIdentityFields}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Typography variant="body2" color="text.secondary">
                  Observaciones
                </Typography>
                <Typography variant="body1">{observationSummary}</Typography>
              </Grid>

              <Grid item>
                <Tooltip
                  title={
                    !hasValidAmount
                      ? "Selecciona primero una cantidad válida para ver las observaciones."
                      : "Editar observaciones del artículo"
                  }
                >
                  <span>
                    <IconButton
                      color={
                        draft.observation_preview_units?.length ? "primary" : "default"
                      }
                      disabled={!hasValidAmount}
                      onClick={() => void handleOpenObservationModal()}
                    >
                      <MdVisibility />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button type="button" variant="outlined" onClick={handleOpen}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit}
              disabled={disabled}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>

        <ObservationRecordsModal
          open={observationModalOpen}
          loading={observationRecordsLoading}
          title="Unidades a reservar"
          subtitle={`${article?.code ?? draft.article_id} - ${article?.description ?? ""} | Cantidad a reservar: ${draft.amount}`}
          records={observationPreviewRecords}
          onClose={() => setObservationModalOpen(false)}
          onSaveRecord={handleSaveObservationRecord}
        />
      </Paper>
    </Modal>
  );
}
